import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import React from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { BulkOperation } from '../apis/types';

import BulkUploadCard from './BulkUploadCard';

interface ImportListProps {
  bulkOperations: BulkOperation[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const BulkUploadList: React.FC<ImportListProps> = ({
  bulkOperations,
  loading,
  error,
  onRefresh,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <LoadingSpinner size='xl' className='mx-auto mb-4' />
          <p className='text-gray-600 dark:text-gray-400'>Loading imports...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='text-xl font-semibold text-red-600 mb-2'>
            Error loading imports
          </div>
          <p className='text-red-500 mb-4'>{error}</p>
          <button
            onClick={onRefresh}
            className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (bulkOperations.length === 0) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center max-w-md'>
          <InboxArrowDownIcon className='w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4' />
          <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2'>
            No imports yet
          </h3>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            Get started by creating new import
          </p>
        </div>
      </div>
    );
  }

  // Sort bulk operations by createdAt (newest first)
  const sortedBulkOperations = [...bulkOperations].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // List of bulk operations
  return (
    <div className='flex flex-col space-y-4'>
      {sortedBulkOperations.map(bulkOperation => (
        <BulkUploadCard key={bulkOperation.id} bulkOperation={bulkOperation} />
      ))}
    </div>
  );
};

export default BulkUploadList;
